import { Block } from '@news-mono/web-common'

/**
 * Returns whether this block contributes text to TTS extraction (same logic as extractTtsText).
 * Used to align paragraph cursor when passing ttsWords to blocks.
 */
export function doesBlockContributeToTts(block: Block): boolean {
    switch (block.kind) {
        case 'text':
        case 'subhead':
            return typeof block.text === 'string' && block.text.trim().length > 0
        case 'ordered-list':
        case 'unordered-list': {
            const items = Array.isArray(block.items) ? block.items : []
            return items.some((item) => {
                if (!item || item.kind !== 'list-item' || !Array.isArray(item.blocks)) return false
                return item.blocks.some((b) => {
                    if (!b) return false
                    if (b.kind === 'text' || b.kind === 'subhead') {
                        return typeof b.text === 'string' && b.text.trim().length > 0
                    }
                    return false
                })
            })
        }
        default:
            return false
    }
}

const FLOATING_BUTTON_HEIGHT = 50
const FLOATING_BUTTON_WIDTH = 200
const FLOATING_SPACING = 40

export interface FloatingPosition {
    top: number
    left: number
}

/**
 * Finds the paragraph element that currently contains the TTS highlight (mark with background).
 * Used for floating TTS controls positioning.
 */
export function findCurrentParagraphElement(
    articleContentID: string,
): HTMLElement | null {
    const articleContent = document.getElementById(articleContentID)
    if (!articleContent) return null
    const highlightedMark = articleContent.querySelector(
        'mark [data-tts-current-word]',
    )
    if (!highlightedMark) return null
    return highlightedMark.closest('p') as HTMLElement
}

/**
 * Computes viewport-relative top/left for the floating TTS button from a paragraph rect.
 */
export function computeFloatingPosition(
    paragraphRect: DOMRect,
    viewportWidth: number,
    viewportHeight: number,
): FloatingPosition {
    let top = paragraphRect.bottom + FLOATING_SPACING
    let left = paragraphRect.left + paragraphRect.width / 2

    if (paragraphRect.bottom > viewportHeight - FLOATING_BUTTON_HEIGHT - FLOATING_SPACING) {
        top = viewportHeight - FLOATING_BUTTON_HEIGHT - FLOATING_SPACING
    }
    if (paragraphRect.top < 0) {
        top = FLOATING_SPACING
    }

    left = Math.max(
        FLOATING_BUTTON_WIDTH / 2,
        Math.min(left, viewportWidth - FLOATING_BUTTON_WIDTH / 2),
    )
    return { top, left }
}

/** Fallback position when no paragraph/highlight is found (e.g. bottom-center). */
export function getDefaultFloatingPosition(
    viewportWidth: number,
    viewportHeight: number,
): FloatingPosition {
    return {
        top: viewportHeight - 100,
        left: viewportWidth / 2,
    }
}

export const POSITION_UPDATE_INTERVAL_MS = 100
